// Third-party imports
import chalk from 'chalk';

// Utility imports
import { logger } from '../utils/logger.js';

// Base error class for all issue-related errors
export class IssueError extends Error {
  constructor(
    message: string,
    public code: string
  ) {
    super(message);
    this.name = 'IssueError';
  }
}

// Specific error classes
export class FileNotFoundError extends IssueError {
  constructor(file: string) {
    super(
      `Could not find file: ${chalk.cyan(file)}\n` +
      `Make sure you're in the correct directory and the file exists.`,
      'FILE_NOT_FOUND'
    );
  }
}

export class InvalidFilenameError extends IssueError {
  constructor(filename: string) {
    super(
      `Invalid filename format: ${chalk.cyan(filename)}\n` +
      `Expected format for active items: {id}-{type}-{state}[-plan-{planid}].md\n` +
      `Expected format for archived items: {reason}-{type}-{id}.md`,
      'INVALID_FILENAME'
    );
  }
}

export class InvalidTargetError extends IssueError {
  constructor(target: string, validTypes: readonly string[], validStates: readonly string[]) {
    super(
      `Invalid target: ${chalk.cyan(target)}\n` +
      `Must be one of:\n` +
      `  Types: ${validTypes.map(t => chalk.cyan(t)).join(', ')}\n` +
      `  States: ${validStates.map(s => chalk.cyan(s)).join(', ')}`,
      'INVALID_TARGET'
    );
  }
}

export class FileExistsError extends IssueError {
  constructor(file: string) {
    super(
      `File already exists: ${chalk.cyan(file)}\n` +
      `Use a different name or remove the existing file first.`,
      'FILE_EXISTS'
    );
  }
}

export class TemplateNotFoundError extends IssueError {
  constructor(type: string, validTypes: readonly string[]) {
    super(
      `Template not found: ${chalk.cyan(type)}\n` +
      `Available templates: ${validTypes.map(t => chalk.cyan(t)).join(', ')}`,
      'TEMPLATE_NOT_FOUND'
    );
  }
}

export class InvalidIssueTypeError extends IssueError {
  constructor(type: string, validTypes: readonly string[]) {
    super(
      `Invalid issue type: ${chalk.red(type)}. Valid types are: ${validTypes.map(t => chalk.green(t)).join(', ')}`,
      'INVALID_TYPE'
    );
  }
}

export class IdCollisionError extends IssueError {
  constructor(id: string) {
    super(
      `A file with ID ${chalk.red(id)} already exists in the inbox`,
      'ID_COLLISION'
    );
  }
}

export class FrontmatterError extends IssueError {
  constructor(message: string) {
    super(
      `Frontmatter error: ${chalk.red(message)}`,
      'INVALID_FRONTMATTER'
    );
  }
}

export class ContentError extends IssueError {
  constructor(message: string) {
    super(
      `Content error: ${chalk.red(message)}`,
      'CONTENT_ERROR'
    );
  }
}

export class InvalidStateError extends IssueError {
  constructor(from: string, to: string) {
    super(
      `Invalid state transition from ${chalk.red(from)} to ${chalk.red(to)}`,
      'INVALID_STATE_TRANSITION'
    );
  }
}

export class StateTransitionError extends IssueError {
  constructor(from: string, to: string, reason: string) {
    super(
      `Cannot transition from ${chalk.red(from)} to ${chalk.red(to)}: ${reason}`,
      'STATE_TRANSITION_ERROR'
    );
  }
}

// Error handling utility
export function handleError(error: unknown): never {
  if (error instanceof IssueError) {
    logger.error(error.message);
    process.exit(1);
  }
  
  if (error instanceof Error) {
    logger.error(error.message);
    process.exit(1);
  }
  
  logger.error('An unknown error occurred');
  process.exit(1);
} 